import React from "react"
import { motion } from "framer-motion"
import { useSelector } from "react-redux"
import {
    HiOutlineKey,
    HiOutlinePencilSquare,
    HiOutlinePlus,
    HiOutlineShieldCheck,
    HiOutlineTrash,
    HiOutlineUserPlus,
    HiOutlineEye,
    HiOutlineEyeSlash,
} from "react-icons/hi2"
import Button from "../../Components/Button"
import Dialog from "../../Components/Dialog"
import TableActionButton from "../../Components/TableActionButton"
import API from "../../Common/API"
import {
    createAdminDraft,
    defaultAdminPermissions,
    getCurrentGlobalAdmin,
    getManagedAdmins,
    removeManagedAdmin,
    resetManagedAdminPassword,
    toggleManagedAdminStatus,
    upsertManagedAdmin,
} from "../../Common/adminUsers"

const formatDate = (value) => {
    if (!value) return "-"

    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return "-"

    return parsed.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    })
}

const summaryCardClass = "rounded-[28px] border border-borderColor bg-white p-5 shadow-soft"

const AdminForm = ({ form, setForm, showPassword = false }) => {
    const [showPw, setShowPw] = React.useState(false)

    const updateField = (key, value) => {
        setForm((current) => ({
            ...current,
            [key]: value,
        }))
    }

    const updatePermission = (key) => {
        setForm((current) => ({
            ...current,
            permissions: {
                ...current.permissions,
                [key]: !current.permissions[key],
            },
        }))
    }

    return (
        <div className="grid gap-5">
            <div className="grid gap-4 md:grid-cols-2">
                <label className="grid gap-2">
                    <span className="text-sm font-semibold text-tColor">Admin name</span>
                    <input
                        className="min-h-[48px] rounded-2xl border border-borderColor bg-white px-4 text-sm font-medium text-tColor outline-none transition focus:border-primeColor"
                        value={form.fullName}
                        onChange={(event) => updateField("fullName", event.target.value)}
                        placeholder="Front desk admin"
                    />
                </label>
                <label className="grid gap-2">
                    <span className="text-sm font-semibold text-tColor">Username</span>
                    <input
                        className="min-h-[48px] rounded-2xl border border-borderColor bg-white px-4 text-sm font-medium text-tColor outline-none transition focus:border-primeColor"
                        value={form.userName}
                        onChange={(event) => updateField("userName", event.target.value)}
                        placeholder="frontdesk.admin"
                    />
                </label>
                <label className="grid gap-2">
                    <span className="text-sm font-semibold text-tColor">Email</span>
                    <input
                        className="min-h-[48px] rounded-2xl border border-borderColor bg-white px-4 text-sm font-medium text-tColor outline-none transition focus:border-primeColor"
                        value={form.email}
                        onChange={(event) => updateField("email", event.target.value)}
                        placeholder="admin@royalkavery.com"
                    />
                </label>
                <label className="grid gap-2">
                    <span className="text-sm font-semibold text-tColor">Phone</span>
                    <input
                        className="min-h-[48px] rounded-2xl border border-borderColor bg-white px-4 text-sm font-medium text-tColor outline-none transition focus:border-primeColor"
                        value={form.phone}
                        onChange={(event) => updateField("phone", event.target.value)}
                        placeholder="9876543210"
                    />
                </label>
                {showPassword && (
                    <label className="grid gap-2">
                        <span className="text-sm font-semibold text-tColor">Password <span className="text-primeColor">*</span></span>
                        <div className="relative">
                            <input
                                type={showPw ? "text" : "password"}
                                className="min-h-[48px] w-full rounded-2xl border border-borderColor bg-white px-4 pr-12 text-sm font-medium text-tColor outline-none transition focus:border-primeColor"
                                value={form.password || ""}
                                onChange={(event) => updateField("password", event.target.value)}
                                placeholder="Min 8 characters"
                                autoComplete="new-password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPw((v) => !v)}
                                className="absolute right-4 top-1/2 -translate-y-1/2 text-tpColor hover:text-tColor"
                            >
                                {showPw ? <HiOutlineEyeSlash className="h-5 w-5" /> : <HiOutlineEye className="h-5 w-5" />}
                            </button>
                        </div>
                    </label>
                )}
                <label className="grid gap-2">
                    <span className="text-sm font-semibold text-tColor">Access level</span>
                    <select
                        className="min-h-[48px] rounded-2xl border border-borderColor bg-white px-4 text-sm font-medium text-tColor outline-none transition focus:border-primeColor"
                        value={form.accessLevel}
                        onChange={(event) => updateField("accessLevel", event.target.value)}
                    >
                        <option value="Operations Admin">Operations Admin</option>
                        <option value="Support Admin">Support Admin</option>
                        <option value="Reports Admin">Reports Admin</option>
                        <option value="Manager Admin">Manager Admin</option>
                    </select>
                </label>
                <label className="grid gap-2">
                    <span className="text-sm font-semibold text-tColor">Status</span>
                    <select
                        className="min-h-[48px] rounded-2xl border border-borderColor bg-white px-4 text-sm font-medium text-tColor outline-none transition focus:border-primeColor"
                        value={form.status}
                        onChange={(event) => updateField("status", event.target.value)}
                    >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                    </select>
                </label>
            </div>

            <label className="grid gap-2">
                <span className="text-sm font-semibold text-tColor">Notes</span>
                <textarea
                    rows="3"
                    className="rounded-2xl border border-borderColor bg-white px-4 py-3 text-sm font-medium text-tColor outline-none transition focus:border-primeColor"
                    value={form.notes}
                    onChange={(event) => updateField("notes", event.target.value)}
                    placeholder="Morning shift, handles user approvals and phone support."
                />
            </label>

            <div className="grid gap-3">
                <p className="text-sm font-semibold text-tColor">Allowed sections</p>
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {defaultAdminPermissions.map((permission) => (
                        <label
                            key={permission.key}
                            className={`flex min-h-[48px] cursor-pointer items-center justify-between rounded-2xl border px-4 py-3 text-sm font-semibold transition ${
                                form.permissions[permission.key]
                                    ? "border-primeColor bg-primeSoft text-primeColor"
                                    : "border-borderColor bg-white text-tColor hover:border-primeColor/30"
                            }`}
                        >
                            <span>{permission.label}</span>
                            <input
                                type="checkbox"
                                className="h-4 w-4 accent-primeColor"
                                checked={form.permissions[permission.key]}
                                onChange={() => updatePermission(permission.key)}
                            />
                        </label>
                    ))}
                </div>
            </div>
        </div>
    )
}

const AdminUsers = () => {
    const userDetails = useSelector((state) => state.auth.userDetails)
    const currentRole = userDetails?.role || getCurrentGlobalAdmin().role
    const globalAdmin = React.useMemo(() => getCurrentGlobalAdmin(), [])
    const [admins, setAdmins] = React.useState(() => getManagedAdmins())
    const [search, setSearch] = React.useState("")
    const [feedback, setFeedback] = React.useState("")
    const [addOpen, setAddOpen] = React.useState(false)
    const [editState, setEditState] = React.useState({ open: false, adminId: "", form: createAdminDraft() })
    const [resetState, setResetState] = React.useState({ open: false, adminId: "", adminName: "", password: "" })
    const [removeState, setRemoveState] = React.useState({ open: false, adminId: "", adminName: "" })
    const feedbackTimerRef = React.useRef(null)

    React.useEffect(() => () => window.clearTimeout(feedbackTimerRef.current), [])

    const showFeedback = (message) => {
        setFeedback(message)
        window.clearTimeout(feedbackTimerRef.current)
        feedbackTimerRef.current = window.setTimeout(() => setFeedback(""), 2600)
    }

    const filteredAdmins = React.useMemo(() => {
        const keywords = search.toLowerCase().trim().split(" ").filter(Boolean)
        if (keywords.length === 0) return admins

        return admins.filter((admin) => {
            const searchString = [
                admin.fullName,
                admin.userName,
                admin.email,
                admin.phone,
                admin.accessLevel,
                admin.notes,
            ]
                .filter(Boolean)
                .join(" ")
                .toLowerCase()

            return keywords.every((keyword) => searchString.includes(keyword))
        })
    }, [admins, search])

    const activeAdmins = admins.filter((admin) => admin.status !== "inactive")
    const inactiveAdmins = admins.filter((admin) => admin.status === "inactive")

    const openEdit = (admin) => {
        setEditState({
            open: true,
            adminId: admin.id,
            form: {
                fullName: admin.fullName || "",
                userName: admin.userName || "",
                email: admin.email || "",
                phone: admin.phone || "",
                accessLevel: admin.accessLevel || "Operations Admin",
                status: admin.status || "active",
                notes: admin.notes || "",
                permissions: defaultAdminPermissions.reduce((acc, permission) => {
                    acc[permission.key] = Boolean(admin?.permissions?.[permission.key])
                    return acc
                }, {}),
            },
        })
    }

    const [addSaving, setAddSaving] = React.useState(false)

    const saveAddAdmin = async (form) => {
        if (!form.password) { showFeedback("Password is required to create an admin login."); return }
        setAddSaving(true)
        try {
            const res = await API.post('/auth/createAdmin', {
                fullName:     form.fullName,
                mobileNumber: form.phone,
                userName:     form.userName,
                password:     form.password,
                email:        form.email,
                role:         'Admin',
            })
            if (res.status === 200) {
                // Also save locally for the UI list
                setAdmins(upsertManagedAdmin(form))
                setAddOpen(false)
                showFeedback(`Admin "${form.fullName}" created. They can now log in with mobile/username and the password you set.`)
            }
        } catch (err) {
            const msg = err?.response?.data?.error || 'Failed to create admin. Please try again.'
            showFeedback(`Error: ${msg}`)
        } finally {
            setAddSaving(false)
        }
    }

    const saveEditAdmin = () => {
        setAdmins(upsertManagedAdmin(editState.form, editState.adminId))
        setEditState({ open: false, adminId: "", form: createAdminDraft() })
        showFeedback("Admin access updated successfully.")
    }

    const toggleStatus = (adminId) => {
        setAdmins(toggleManagedAdminStatus(adminId))
        showFeedback("Admin status updated.")
    }

    const saveResetPassword = async () => {
        const newPw = resetState.password
        if (newPw && newPw.length >= 8) {
            // Try real backend reset first
            try {
                await API.post('/auth/resetAdminPassword', { adminId: resetState.adminId, newPassword: newPw })
            } catch (err) {
                showFeedback(`Error: ${err?.response?.data?.error || 'Password reset failed'}`)
                return
            }
        }
        const result = resetManagedAdminPassword(resetState.adminId, resetState.password)
        setAdmins(result.admins)
        setResetState({ open: false, adminId: "", adminName: "", password: "" })
        showFeedback(`Password updated successfully for ${resetState.adminName}.`)
    }

    const confirmRemove = () => {
        setAdmins(removeManagedAdmin(removeState.adminId))
        setRemoveState({ open: false, adminId: "", adminName: "" })
        showFeedback("Admin removed from the access list.")
    }

    if (currentRole !== "Super_Admin") {
        return (
            <div className="panel-card p-8 text-center">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-primeColor">Restricted</p>
                <h1 className="mt-3 text-3xl font-semibold text-tColor">Global Admin access only</h1>
                <p className="mt-4 text-base leading-8 text-tpColor">
                    Only the Global Admin can view, add, remove, or reset admin access from this screen.
                </p>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            <div className="panel-card overflow-hidden p-5 sm:p-6">
                <div className="grid gap-6 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
                    <div>
                        <p className="text-xs font-bold uppercase tracking-[0.18em] text-primeColor">Global Admin</p>
                        <h1 className="mt-2 text-3xl font-semibold text-tColor sm:text-4xl">Admin access control</h1>
                        <p className="mt-3 max-w-3xl text-base leading-8 text-tpColor">
                            This page is only for the Global Admin. Add admins, update username access, pause admin accounts, and reset passwords from one place.
                        </p>

                        <div className="mt-6 grid gap-4 sm:grid-cols-3">
                            <div className={summaryCardClass}>
                                <p className="text-xs font-bold uppercase tracking-[0.14em] text-primeColor">Total admins</p>
                                <p className="mt-2 text-3xl font-semibold text-tColor">{admins.length}</p>
                            </div>
                            <div className={summaryCardClass}>
                                <p className="text-xs font-bold uppercase tracking-[0.14em] text-tpColor">Active admins</p>
                                <p className="mt-2 text-3xl font-semibold text-tColor">{activeAdmins.length}</p>
                            </div>
                            <div className={summaryCardClass}>
                                <p className="text-xs font-bold uppercase tracking-[0.14em] text-tpColor">Paused admins</p>
                                <p className="mt-2 text-3xl font-semibold text-tColor">{inactiveAdmins.length}</p>
                            </div>
                        </div>
                    </div>

                    <div className="rounded-[30px] bg-gradient-to-br from-[#fff8f2] via-[#fffdfa] to-[#eef6fb] p-5 shadow-soft">
                        <div className="flex items-center gap-3">
                            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-primeColor shadow-soft">
                                <HiOutlineShieldCheck className="text-2xl" />
                            </div>
                            <div>
                                <p className="text-xs font-bold uppercase tracking-[0.18em] text-primeColor">Current owner</p>
                                <h2 className="mt-1 text-2xl font-semibold text-tColor">{globalAdmin.name}</h2>
                            </div>
                        </div>
                        <div className="mt-5 space-y-3 rounded-2xl bg-white/80 p-4">
                            <p className="text-sm font-semibold text-tColor">Username: <span className="text-tpColor">{globalAdmin.userName}</span></p>
                            <p className="text-sm font-semibold text-tColor">Email: <span className="text-tpColor">{globalAdmin.email}</span></p>
                            <p className="text-sm font-semibold text-tColor">Role: <span className="text-tpColor">{globalAdmin.role}</span></p>
                        </div>
                        <p className="mt-4 text-sm leading-7 text-tpColor">
                            Global Admin is shown here for reference only. Managed admins are listed below and can be updated from this screen.
                        </p>
                    </div>
                </div>
            </div>

            {feedback ? (
                <div className="rounded-3xl border border-[#d6e9d7] bg-[#f2fbf2] px-5 py-4 text-sm font-semibold text-[#2b6d35] shadow-soft">
                    {feedback}
                </div>
            ) : null}

            <div className="panel-card p-5 sm:p-6">
                <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
                    <div>
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-primeColor">Admin list</p>
                        <h2 className="mt-2 text-2xl font-semibold text-tColor">Managed admins</h2>
                        <p className="mt-2 text-sm leading-7 text-tpColor">
                            Search by name, username, phone, or access level. Use the action buttons to edit, reset password, pause, or remove.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <input
                            className="min-h-[48px] min-w-[280px] rounded-2xl border border-borderColor bg-white px-4 text-sm font-medium text-tColor outline-none transition focus:border-primeColor"
                            placeholder="Search admins"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                        />
                        <Button type="primary" injectClass="rounded-2xl" onClick={() => setAddOpen(true)}>
                            <HiOutlinePlus className="text-lg" />
                            Add admin
                        </Button>
                    </div>
                </div>

                <div className="mt-6 overflow-hidden rounded-[28px] border border-borderColor">
                    <div className="hidden bg-slate-50 px-5 py-4 text-xs font-bold uppercase tracking-[0.14em] text-tpColor md:grid md:grid-cols-[1.2fr_0.9fr_0.8fr_0.9fr_0.8fr_180px]">
                        <span>Admin</span>
                        <span>Username</span>
                        <span>Access</span>
                        <span>Contact</span>
                        <span>Status</span>
                        <span className="text-right">Actions</span>
                    </div>

                    <div className="divide-y divide-borderColor bg-white">
                        {filteredAdmins.length > 0 ? filteredAdmins.map((admin) => {
                            const permissionCount = Object.values(admin.permissions || {}).filter(Boolean).length
                            return (
                                <motion.div
                                    key={admin.id}
                                    layout
                                    className="grid gap-4 px-5 py-5 md:grid-cols-[1.2fr_0.9fr_0.8fr_0.9fr_0.8fr_180px] md:items-center"
                                >
                                    <div>
                                        <p className="text-base font-semibold text-tColor">{admin.fullName || "-"}</p>
                                        <p className="mt-1 text-sm text-tpColor">
                                            Added {formatDate(admin.createdAt)} • {permissionCount} section{permissionCount === 1 ? "" : "s"}
                                        </p>
                                        {admin.notes ? <p className="mt-2 text-sm text-tpColor">{admin.notes}</p> : null}
                                    </div>
                                    <div className="text-sm font-semibold text-tColor">{admin.userName || "-"}</div>
                                    <div className="text-sm font-semibold text-tColor">{admin.accessLevel || "-"}</div>
                                    <div className="text-sm text-tColor">
                                        <p className="font-semibold">{admin.phone || "-"}</p>
                                        <p className="mt-1 text-tpColor">{admin.email || "-"}</p>
                                    </div>
                                    <div>
                                        <button
                                            type="button"
                                            className={`inline-flex min-h-[38px] items-center rounded-full px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] ${
                                                admin.status === "inactive"
                                                    ? "bg-slate-200 text-slate-700"
                                                    : "bg-[#e8f8ef] text-[#1c7d52]"
                                            }`}
                                            onClick={() => toggleStatus(admin.id)}
                                        >
                                            {admin.status === "inactive" ? "Inactive" : "Active"}
                                        </button>
                                        <p className="mt-2 text-xs text-tpColor">Reset: {formatDate(admin.passwordResetAt)}</p>
                                    </div>
                                    <div className="flex items-center justify-start gap-2 md:justify-end">
                                        <TableActionButton title="Edit admin" tone="primary" onClick={() => openEdit(admin)}>
                                            <HiOutlinePencilSquare />
                                        </TableActionButton>
                                        <TableActionButton
                                            title="Reset password"
                                            tone="neutral"
                                            onClick={() => setResetState({ open: true, adminId: admin.id, adminName: admin.fullName, password: "" })}
                                        >
                                            <HiOutlineKey />
                                        </TableActionButton>
                                        <TableActionButton
                                            title={admin.status === "inactive" ? "Activate admin" : "Pause admin"}
                                            tone={admin.status === "inactive" ? "success" : "neutral"}
                                            onClick={() => toggleStatus(admin.id)}
                                        >
                                            <HiOutlineShieldCheck />
                                        </TableActionButton>
                                        <TableActionButton
                                            title="Remove admin"
                                            tone="danger"
                                            onClick={() => setRemoveState({ open: true, adminId: admin.id, adminName: admin.fullName })}
                                        >
                                            <HiOutlineTrash />
                                        </TableActionButton>
                                    </div>
                                </motion.div>
                            )
                        }) : (
                            <div className="px-5 py-10 text-center text-tpColor">
                                No admin users added yet. Use <span className="font-semibold text-tColor">Add admin</span> to create the first managed admin.
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Dialog showDialog={addOpen} onClose={() => setAddOpen(false)} title="Add admin user" injectClass="max-w-5xl">
                <AdminCreateDialogBody
                    onCancel={() => setAddOpen(false)}
                    onSave={saveAddAdmin}
                    saving={addSaving}
                />
            </Dialog>

            <Dialog
                showDialog={editState.open}
                onClose={() => setEditState({ open: false, adminId: "", form: createAdminDraft() })}
                title="Edit admin user"
                injectClass="max-w-5xl"
            >
                <div className="grid gap-5">
                    <AdminForm form={editState.form} setForm={(updater) => setEditState((current) => ({
                        ...current,
                        form: typeof updater === "function" ? updater(current.form) : updater,
                    }))} />
                    <div className="flex flex-wrap justify-end gap-3 border-t border-borderColor pt-4">
                        <Button
                            type="secondary"
                            injectClass="rounded-full px-5"
                            onClick={() => setEditState({ open: false, adminId: "", form: createAdminDraft() })}
                        >
                            Cancel
                        </Button>
                        <Button type="primary" injectClass="rounded-full px-5" onClick={saveEditAdmin}>
                            Save admin
                        </Button>
                    </div>
                </div>
            </Dialog>

            <Dialog
                showDialog={resetState.open}
                onClose={() => setResetState({ open: false, adminId: "", adminName: "", password: "" })}
                title="Reset admin password"
                injectClass="max-w-2xl"
            >
                <div className="grid gap-5">
                    <div className="rounded-2xl bg-slate-50 p-4 text-sm leading-7 text-tColor">
                        Reset password for <span className="font-semibold">{resetState.adminName || "this admin"}</span>. Leave the field empty if you want the system to generate a temporary password.
                    </div>
                    <label className="grid gap-2">
                        <span className="text-sm font-semibold text-tColor">New password</span>
                        <input
                            className="min-h-[48px] rounded-2xl border border-borderColor bg-white px-4 text-sm font-medium text-tColor outline-none transition focus:border-primeColor"
                            value={resetState.password}
                            onChange={(event) => setResetState((current) => ({ ...current, password: event.target.value }))}
                            placeholder="Leave empty to auto-generate"
                        />
                    </label>
                    <div className="flex flex-wrap justify-end gap-3 border-t border-borderColor pt-4">
                        <Button type="secondary" injectClass="rounded-full px-5" onClick={() => setResetState({ open: false, adminId: "", adminName: "", password: "" })}>
                            Cancel
                        </Button>
                        <Button type="primary" injectClass="rounded-full px-5" onClick={saveResetPassword}>
                            Reset password
                        </Button>
                    </div>
                </div>
            </Dialog>

            <Dialog
                showDialog={removeState.open}
                onClose={() => setRemoveState({ open: false, adminId: "", adminName: "" })}
                title="Remove admin user"
                injectClass="max-w-xl"
            >
                <div className="grid gap-5">
                    <p className="text-sm leading-7 text-tColor">
                        Remove <span className="font-semibold">{removeState.adminName || "this admin"}</span> from the managed admin access list?
                    </p>
                    <div className="flex flex-wrap justify-end gap-3 border-t border-borderColor pt-4">
                        <Button type="secondary" injectClass="rounded-full px-5" onClick={() => setRemoveState({ open: false, adminId: "", adminName: "" })}>
                            Cancel
                        </Button>
                        <Button type="danger" injectClass="rounded-full px-5" onClick={confirmRemove}>
                            Remove
                        </Button>
                    </div>
                </div>
            </Dialog>
        </div>
    )
}

const AdminCreateDialogBody = ({ onCancel, onSave, saving }) => {
    const [form, setForm] = React.useState({ ...createAdminDraft(), password: "" })

    return (
        <div className="grid gap-5">
            <AdminForm form={form} setForm={setForm} showPassword={true} />
            <div className="flex flex-wrap justify-end gap-3 border-t border-borderColor pt-4">
                <Button type="secondary" injectClass="rounded-full px-5" onClick={onCancel}>
                    Cancel
                </Button>
                <Button type="primary" injectClass="rounded-full px-5" disabled={saving} onClick={() => onSave(form)}>
                    <HiOutlineUserPlus className="text-lg" />
                    {saving ? 'Creating…' : 'Create admin'}
                </Button>
            </div>
        </div>
    )
}

export default AdminUsers
